/**
 * After an answer that wrote data, let the app's open views (e.g. the
 * template editor behind the panel) show the change without a reload — and,
 * if a write deleted the item currently open in the route, leave that route
 * first so no query asks the server for an item that no longer exists.
 *
 * Domain-neutral (`ai-assistant-chat-panel` spec): driven only by a tool
 * call's `effect` and a successful write's JSON result `deletedItemIds`,
 * never by tool names or by which item a write's *input* merely names (an
 * update of the open item must not navigate away).
 */
import { useApolloClient } from "@apollo/client";
import React from "react";
import { useNavigate } from "react-router-dom";
import type { AgentToolCall } from "./agentStream.js";

/** Successful writes: `effect: "write"` and a `resultPreview` that is not an error. */
export function getSuccessfulWriteToolCalls(
  toolCalls: readonly AgentToolCall[]
): AgentToolCall[] {
  return toolCalls.filter(
    call => call.effect === "write" && !call.resultPreview.startsWith("ERROR")
  );
}

export type WriteDeletions = {
  /** Distinct item ids a write's JSON result reported as `deletedItemIds`. */
  deletedItemIds: string[];
  /**
   * True when a write's `resultPreview` looks like it reported deletions
   * but couldn't be parsed as JSON. The backend clamps `resultPreview` to
   * 1,500 chars, so a large `deletedItemIds` array can be truncated
   * mid-JSON. The specific ids are then unknown, but the write did delete
   * *something* — enough to no longer trust a currently open item route.
   */
  deletedSomethingUnknown: boolean;
};

/** Safely parses JSON text; `null` when it isn't valid JSON. */
function tryParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * The `deletedItemIds` reported by each of the given (already-successful)
 * write calls' JSON `resultPreview`, plus whether an unparsable preview
 * still looks like a truncated `deletedItemIds` array (see
 * `WriteDeletions.deletedSomethingUnknown`). A write's `input` (e.g. an
 * `itemId` naming the item it updated) is never consulted here.
 */
export function getWriteDeletions(
  toolCalls: readonly AgentToolCall[]
): WriteDeletions {
  const deletedItemIds = new Set<string>();
  let deletedSomethingUnknown = false;

  for (const call of toolCalls) {
    const parsed = tryParseJson(call.resultPreview);
    if (parsed !== null && typeof parsed === "object") {
      const ids = (parsed as Record<string, unknown>).deletedItemIds;
      if (Array.isArray(ids)) {
        for (const id of ids) {
          if (typeof id === "string") deletedItemIds.add(id);
        }
        continue;
      }
    }
    if (call.resultPreview.includes('"deletedItemIds"')) {
      deletedSomethingUnknown = true;
    }
  }

  return { deletedItemIds: [...deletedItemIds], deletedSomethingUnknown };
}

const OPEN_ITEM_ROUTE = /^(.*)\/items\/([0-9a-f-]{36})/;

/** The parent route and item id when `pathname` points at an open item. */
export function matchOpenItemRoute(
  pathname: string
): { parentPath: string; itemId: string } | null {
  const match = OPEN_ITEM_ROUTE.exec(pathname);
  const parentPath = match?.[1];
  const itemId = match?.[2];
  if (!parentPath || !itemId) return null;
  return { parentPath, itemId };
}

/**
 * Whether a query with these variables should be refetched: a query naming a
 * deleted (or just-left-open) item id in any of its variables is skipped.
 */
export function shouldRefetchQuery(
  variables: Record<string, unknown> | undefined,
  goneItemIds: ReadonlySet<string>
): boolean {
  // Item ids also travel inside inputs and lists (`{ input: { itemId } }`).
  const namesGoneItem = (value: unknown): boolean => {
    if (typeof value === "string") return goneItemIds.has(value);
    if (Array.isArray(value)) return value.some(namesGoneItem);
    if (typeof value === "object" && value !== null)
      return Object.values(value).some(namesGoneItem);
    return false;
  };
  return !namesGoneItem(variables ?? {});
}

const ITEM_ROUTE_WAIT_DEADLINE_MS = 2000;

/**
 * The router may render the new route in a transition: wait until the item
 * route is really gone, so its query is no longer active.
 */
async function waitForItemRouteToClear(): Promise<void> {
  const deadline = Date.now() + ITEM_ROUTE_WAIT_DEADLINE_MS;
  while (
    window.location.pathname.includes("/items/") &&
    Date.now() < deadline
  ) {
    await new Promise(resolve => requestAnimationFrame(resolve));
  }
}

/**
 * Returns a callback for a result's `toolCalls`: a no-op unless at least one
 * call succeeded with `effect: "write"`. Otherwise, if a write deleted the
 * item currently open in the route, leaves that route first; then refetches
 * every active query except one naming a deleted (or just-left-open) item.
 *
 * The open route is read from `window.location.pathname` at call time —
 * i.e. when the result arrives, not when the message was sent — since the
 * user may have navigated elsewhere while the assistant was working.
 */
export function useRefreshAfterAgentWrite() {
  const apollo = useApolloClient();
  const navigate = useNavigate();

  return React.useCallback(
    async (toolCalls: readonly AgentToolCall[]) => {
      const successfulWrites = getSuccessfulWriteToolCalls(toolCalls);
      if (successfulWrites.length === 0) return;

      const { deletedItemIds, deletedSomethingUnknown } =
        getWriteDeletions(successfulWrites);
      const goneItemIds = new Set(deletedItemIds);

      if (deletedItemIds.length > 0 || deletedSomethingUnknown) {
        const open = matchOpenItemRoute(window.location.pathname);
        if (open) {
          goneItemIds.add(open.itemId);
          navigate(open.parentPath, { replace: true });
          await waitForItemRouteToClear();
        }
      }

      await apollo.refetchQueries({
        include: "active",
        onQueryUpdated: query =>
          shouldRefetchQuery(query.variables, goneItemIds),
      });
    },
    [apollo, navigate]
  );
}
